import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Modal, Button, Field, Input, Select, Spinner } from "./ui";
import { TEMPLATES, COUNTRIES, getCountry, getTemplate, COLOR_SWATCHES, ALL_SECTIONS, SPACES_LIBRARY, SPACES_BY_KEY, getTemplateSpaceKeys } from "../lib/templates";
import { cn } from "../lib/utils";
import { Check } from "lucide-react";

function lstr(obj, lang) {
  if (!obj) return "";
  return obj[lang] ?? obj.fr ?? "";
}

export default function EntityFormModal({ open, onClose, onSaved, editing }) {
  const { t, lang } = useApp();
  const isEdit = !!editing;

  const [template, setTemplate] = useState("consulting");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [legalForm, setLegalForm] = useState("");
  const [taxRegime, setTaxRegime] = useState("");
  const [color, setColor] = useState("#10B981");
  const [description, setDescription] = useState("");
  const [baseCurrency, setBaseCurrency] = useState("EUR");
  const [sections, setSections] = useState([]);
  const [spaceKeys, setSpaceKeys] = useState([]);
  const [withChecklist, setWithChecklist] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTemplate(editing.type || "custom");
      setName(editing.name || "");
      setCountry(editing.country || "");
      setLegalForm(editing.legal_form || "");
      setTaxRegime(editing.tax_regime || "");
      setColor(editing.color || "#10B981");
      setDescription(editing.description || "");
      setBaseCurrency(editing.base_currency || (editing.country === "US" ? "USD" : "EUR"));
      setSections(
        Array.isArray(editing.tables) && editing.tables.length
          ? ALL_SECTIONS.filter((s) => editing.tables.includes(s))
          : getTemplate(editing.type || "custom").tables
      );
    } else {
      setTemplate("consulting");
      setName("");
      setCountry("");
      setLegalForm("");
      setTaxRegime("");
      setColor("#10B981");
      setDescription("");
      setBaseCurrency("EUR");
      setSections(getTemplate("consulting").tables);
      setSpaceKeys(getTemplateSpaceKeys("consulting"));
      setWithChecklist(true);
    }
    setError("");
  }, [open, editing]);

  const selectTemplate = (key) => {
    setTemplate(key);
    setSections(getTemplate(key).tables);
    setSpaceKeys(getTemplateSpaceKeys(key));
    if (!isEdit) setColor(getTemplate(key).color);
    if (getTemplate(key)?.personal) { setLegalForm(""); setTaxRegime(""); }
  };

  const toggleSpace = (k) => {
    setSpaceKeys((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const toggleSection = (s) => {
    setSections((prev) =>
      prev.includes(s)
        ? prev.filter((x) => x !== s)
        : ALL_SECTIONS.filter((x) => prev.includes(x) || x === s)
    );
  };

  const countryObj = getCountry(country);
  const isPersonal = !!getTemplate(template)?.personal;

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError("");
    try {
      let result;
      if (isEdit) {
        const { data } = await api.put(`/entities/${editing.id}`, {
          name: name.trim(), country: country || null, legal_form: legalForm || null,
          tax_regime: taxRegime || null, color, description,
          tables: sections.length ? sections : null, base_currency: baseCurrency,
        });
        result = data;
      } else {
        const { data } = await api.post("/entities", {
          name: name.trim(), template, country: country || null,
          legal_form: legalForm || null, tax_regime: taxRegime || null,
          color, description, base_currency: baseCurrency,
          tables: sections.length ? sections : null,
          spaces: spaceKeys.map((k) => {
            const s = SPACES_BY_KEY[k];
            return { name: lstr(s.label, lang), description: lstr(s.desc, lang), icon: s.icon };
          }),
          starter_checklist: withChecklist,
          lang,
        });
        result = data;
      }
      onSaved(result, !isEdit && withChecklist);
      onClose();
    } catch {
      setError(lang === "fr" ? "Échec de l'enregistrement. Réessayez." : "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t("edit_entity") : t("create_entity")}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving || !name.trim()} data-testid="entity-save">
            {saving && <Spinner className="w-3.5 h-3.5" />}
            {isEdit ? t("save") : t("create")}
          </Button>
        </>
      }
    >
      {!isEdit && (
        <Field label={t("business_type")}>
          <div className="grid grid-cols-2 gap-2">
            {TEMPLATES.map((tp) => {
              const Icon = tp.icon;
              const on = template === tp.key;
              return (
                <button
                  key={tp.key}
                  type="button"
                  onClick={() => selectTemplate(tp.key)}
                  data-testid={`template-${tp.key}`}
                  className={cn(
                    "flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all",
                    on ? "border-transparent ring-2" : "border-[var(--border)] hover:bg-surface-hover"
                  )}
                  style={on ? { boxShadow: `0 0 0 2px ${tp.color}` } : {}}
                >
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${tp.color}22` }}>
                    <Icon className="w-4 h-4" style={{ color: tp.color }} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-tight">{lstr(tp.label, lang)}</span>
                    <span className="block text-[11px] text-text-secondary mt-0.5 leading-snug">{lstr(tp.desc, lang)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Field>
      )}

      <Field label={t("entity_name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="entity-name" maxLength={80}
          placeholder={lang === "fr" ? "ex : Ma SCI immobilière" : "e.g. My Real-Estate SCI"} />
      </Field>

      <Field label={t("sections_label")}>
        <p className="text-[11px] text-text-secondary -mt-1 mb-2">{t("sections_hint")}</p>
        <div className="flex flex-wrap gap-2" data-testid="sections-selector">
          {ALL_SECTIONS.map((s) => {
            const on = sections.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleSection(s)}
                data-testid={`section-${s}`}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                  on ? "bg-text-primary text-background border-transparent" : "text-text-secondary border-[var(--border)] hover:text-text-primary hover:bg-surface-hover"
                )}
              >
                {on && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                {t(`res_${s}`)}
              </button>
            );
          })}
        </div>
      </Field>

      {!isEdit && (
        <Field label={t("spaces_label")}>
          <p className="text-[11px] text-text-secondary -mt-1 mb-2">{t("spaces_form_hint")}</p>
          <div className="flex flex-wrap gap-2" data-testid="spaces-selector">
            {SPACES_LIBRARY.map((s) => {
              const on = spaceKeys.includes(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggleSpace(s.key)}
                  data-testid={`space-pick-${s.key}`}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                    on ? "bg-text-primary text-background border-transparent" : "text-text-secondary border-[var(--border)] hover:text-text-primary hover:bg-surface-hover"
                  )}
                >
                  {on && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                  {lstr(s.label, lang)}
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {error && (
        <div className="mb-4 -mt-1 text-xs text-red-400" data-testid="entity-form-error">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label={`${t("country")} ${t("optional")}`}>
          <Select value={country} onChange={(e) => { setCountry(e.target.value); setLegalForm(""); setTaxRegime(""); setBaseCurrency(e.target.value === "US" ? "USD" : "EUR"); }} data-testid="entity-country">
            <option value="">{t("none")}</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{lstr(c.label, lang)}</option>)}
          </Select>
        </Field>
        {isPersonal ? (
          <Field label={t("entity_base_currency")}>
            <Select value={baseCurrency} onChange={(e) => setBaseCurrency(e.target.value)} data-testid="entity-base-currency">
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
            </Select>
          </Field>
        ) : (
          <Field label={`${t("legal_form")} ${t("optional")}`}>
            <Select value={legalForm} onChange={(e) => setLegalForm(e.target.value)} disabled={!countryObj} data-testid="entity-legal-form">
              <option value="">{t("none")}</option>
              {countryObj?.legal_forms.map((f) => <option key={f} value={f}>{f}</option>)}
            </Select>
          </Field>
        )}
      </div>

      {!isPersonal && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={`${t("tax_regime")} ${t("optional")}`}>
            <Select value={taxRegime} onChange={(e) => setTaxRegime(e.target.value)} disabled={!countryObj} data-testid="entity-tax-regime">
              <option value="">{t("none")}</option>
              {countryObj?.tax_regimes.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </Field>
          <Field label={t("entity_base_currency")}>
            <Select value={baseCurrency} onChange={(e) => setBaseCurrency(e.target.value)} data-testid="entity-base-currency">
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
            </Select>
          </Field>
        </div>
      )}

      <Field label={t("color")}>
        <div className="flex flex-wrap gap-2">
          {COLOR_SWATCHES.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)} data-testid={`color-${c}`}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ backgroundColor: c, outline: color === c ? `2px solid var(--text-primary)` : "none", outlineOffset: "2px" }}>
              {color === c && <Check className="w-3.5 h-3.5 text-white" />}
            </button>
          ))}
        </div>
      </Field>

      <Field label={t("description") + " " + t("optional")}>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="entity-description" />
      </Field>

      {!isEdit && (
        <button
          type="button"
          onClick={() => setWithChecklist((v) => !v)}
          data-testid="starter-checklist-toggle"
          role="switch"
          aria-checked={withChecklist}
          className="w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors hover:bg-surface-hover"
        >
          <span
            className={cn(
              "mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors",
              withChecklist ? "bg-text-primary border-transparent" : "border-[var(--border-strong)]"
            )}
          >
            {withChecklist && <Check className="w-3.5 h-3.5 text-background" />}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium">{t("starter_checklist")}</span>
            <span className="block text-[11px] text-text-secondary mt-0.5 leading-snug">{t("starter_checklist_hint")}</span>
          </span>
        </button>
      )}
    </Modal>
  );
}
